import React, { useRef, useState } from 'react';
import { ChevronDown, GripVertical, Trash2, X } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { cn } from '@cotera/client/v0/lib/utils';
import type { OrganizeProposal } from '@cotera/client/app/etc/api/typed-client';
import { ResourceTypeIconBubble } from '../resources/type/resource-type-icon-bubble';
import {
  RESOURCE_TYPE_CONFIG,
  type NonFolderResourceType,
} from '../resources/type/resource-type-config';

/**
 * The presentational pieces of the Organize review — one collapsible proposed
 * folder card with drag-and-drop, inline rename, and per-item dismiss. Shared by
 * the Coco draft panel (the shipped flow) so the review UI lives in one place.
 */

export type ProposalMember = OrganizeProposal['members'][number];

/** Stable per-member key (a document and agent could in theory share a uuid). */
export const memberKey = (type: ResourceType, id: string): string =>
  `${type}:${id}`;

/** Inverse of {@link memberKey}. Null when the key isn't a `type:id` pair. */
export const parseMemberKey = (
  key: string
): { type: ResourceType; id: string } | null => {
  const idx = key.indexOf(':');
  if (idx < 0) {
    return null;
  }
  return { type: key.slice(0, idx) as ResourceType, id: key.slice(idx + 1) };
};

/** MIME-ish key we stash the dragged member under on the drag event. */
export const DRAG_KEY = 'application/x-organize-member';

/** MIME-ish key for dragging a whole folder (to re-parent / nest it). */
export const DRAG_FOLDER_KEY = 'application/x-organize-folder';

/**
 * Click-to-edit folder name, styled to match the app's inline rename (the
 * editable breadcrumb): reads as the folder title at rest, opens into an
 * outlined `bg-card` field on click, sized to its content. Enter/blur commit,
 * Escape cancels. Commits are local (applied on Move), so there's no request.
 */
const EditableFolderName: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [draft, setDraft] = useState<string | null>(null);
  // Distinguishes a blur we caused (Enter/Escape) from the user clicking away.
  const closedItselfRef = useRef(false);

  const close = (commit: boolean): void => {
    const next = draft?.trim() ?? '';
    closedItselfRef.current = true;
    setDraft(null);
    if (!commit || next === '' || next === value) {
      return;
    }
    onChange(next);
  };

  if (draft === null) {
    return (
      <button
        type="button"
        title={`Rename “${value}”`}
        onClick={(e) => {
          e.stopPropagation();
          closedItselfRef.current = false;
          setDraft(value);
        }}
        className="max-w-xs cursor-text truncate rounded-md px-1.5 py-0.5 text-left text-sm font-semibold text-slate-800 transition-colors hover:bg-sidebar-accent"
      >
        {value}
      </button>
    );
  }

  return (
    <input
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={draft}
      aria-label="Folder name"
      size={Math.min(Math.max(draft.length + 1, 12), 60)}
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (closedItselfRef.current) {
          closedItselfRef.current = false;
          return;
        }
        close(true);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          close(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          close(false);
        }
      }}
      className="field-sizing-content min-w-0 max-w-xs rounded-md border border-input bg-card px-1.5 py-0.5 text-sm font-semibold text-slate-800 outline-none"
    />
  );
};

export const ProposalCard: React.FC<{
  proposal: OrganizeProposal;
  members: ProposalMember[];
  folderName: string;
  onFolderNameChange: (name: string) => void;
  /** Whether this whole folder is included in the move. */
  selected: boolean;
  onToggleFolder: (value: boolean) => void;
  onDismissMember: (type: ResourceType, id: string) => void;
  onDropMember: (key: string) => void;
  /** When set, the folder header shows a delete control. */
  onDeleteFolder?: () => void;
  /** Rendered inside this folder's expanded area — its nested subfolder cards. */
  subfolders?: React.ReactNode;
  /** How many direct subfolders this folder has (for the header badge). */
  subfolderCount?: number;
  /** Drop another folder (by draftId) onto this one to nest it here. */
  onDropFolder?: (droppedDraftId: string) => void;
}> = ({
  proposal,
  members,
  folderName,
  onFolderNameChange,
  selected,
  onToggleFolder,
  onDismissMember,
  onDropMember,
  onDeleteFolder,
  subfolders,
  subfolderCount = 0,
  onDropFolder,
}) => {
  const folderDraftId = proposal.groupId;
  // Collapsed by default: the review is about approving the folders, so it leads
  // with a scannable list of them and you expand only the ones you want to
  // inspect or rearrange.
  const [open, setOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const intoExisting = proposal.existingFolderId !== null;

  // One understated text summary instead of count pills: subfolders first, then
  // items, joined only when a folder has both. Empty for an empty leaf folder.
  const countSummary = [
    subfolderCount > 0
      ? `${subfolderCount} ${subfolderCount === 1 ? 'folder' : 'folders'}`
      : null,
    members.length > 0
      ? `${members.length} ${members.length === 1 ? 'item' : 'items'}`
      : null,
  ]
    .filter((part) => part !== null)
    .join(' · ');

  const acceptsMemberDrag = (e: React.DragEvent): boolean =>
    e.dataTransfer.types.includes(DRAG_KEY);
  // A folder drag is a candidate drop here only if it isn't this folder itself
  // (dropping onto yourself is a no-op); deeper cycle checks happen on drop.
  const acceptsFolderDrag = (e: React.DragEvent): boolean =>
    onDropFolder !== undefined &&
    e.dataTransfer.types.includes(DRAG_FOLDER_KEY);

  return (
    <section
      className={cn(
        'overflow-hidden rounded-lg border border-border transition-colors',
        dragOver && 'border-primary ring-1 ring-primary'
      )}
      onDragOver={(e) => {
        if (!acceptsMemberDrag(e) && !acceptsFolderDrag(e)) {
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!dragOver) {
          setDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        // Only clear when the pointer actually leaves the card, not its children.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setDragOver(false);
        }
      }}
      onDrop={(e) => {
        const memberKey = acceptsMemberDrag(e)
          ? e.dataTransfer.getData(DRAG_KEY)
          : '';
        const droppedFolderId = acceptsFolderDrag(e)
          ? e.dataTransfer.getData(DRAG_FOLDER_KEY)
          : '';
        if (memberKey === '' && droppedFolderId === '') {
          return;
        }
        e.preventDefault();
        // Nesting takes precedence over bubbling to the panel's top-level drop.
        e.stopPropagation();
        setDragOver(false);
        if (memberKey !== '') {
          onDropMember(memberKey);
        } else if (
          droppedFolderId !== '' &&
          droppedFolderId !== folderDraftId
        ) {
          onDropFolder?.(droppedFolderId);
        }
      }}
    >
      {/* Header — the destination folder. Click to expand/collapse. */}
      <div
        className="group flex cursor-pointer items-center gap-2.5 px-3 py-2.5 hover:bg-secondary/30"
        onClick={() => setOpen((v) => !v)}
      >
        {/* Drag the folder (and its subtree) onto another folder to nest it, or
            onto empty space to move it back to the top level. */}
        <span
          draggable
          onClick={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.dataTransfer.setData(DRAG_FOLDER_KEY, folderDraftId);
            e.dataTransfer.effectAllowed = 'move';
          }}
          title="Drag to move this folder"
          className="shrink-0 cursor-grab text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </span>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={(value) => onToggleFolder(value === true)}
            aria-label={`Include folder ${folderName}`}
          />
        </span>
        {intoExisting ? (
          <span className="max-w-xs truncate text-sm font-semibold text-slate-800">
            {proposal.existingFolderName}
          </span>
        ) : (
          <EditableFolderName
            value={folderName}
            onChange={onFolderNameChange}
          />
        )}
        <span className="min-w-0 flex-1" aria-hidden="true" />
        {countSummary !== '' ? (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {countSummary}
          </span>
        ) : null}
        {onDeleteFolder !== undefined ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteFolder();
            }}
            aria-label={`Delete folder ${folderName}`}
            title="Delete folder"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open ? '' : '-rotate-90'
          )}
          aria-hidden="true"
        />
      </div>

      {open ? (
        <div className="border-t border-border/60 px-2 py-1.5">
          {members.length === 0 && subfolderCount === 0 ? (
            <div className="px-2 py-6 text-center text-xs text-muted-foreground">
              This folder is empty. Drag items here to add them.
            </div>
          ) : null}
          {members.map((member) => {
            const resourceType = member.type as NonFolderResourceType;
            const icon = RESOURCE_TYPE_CONFIG[resourceType]?.icon;
            const location = member.currentFolderName ?? 'Library';
            const key = memberKey(member.type, member.id);
            return (
              <div
                key={key}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_KEY, key);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-secondary/40"
              >
                <GripVertical
                  className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground"
                  aria-hidden="true"
                />
                {icon !== undefined ? (
                  <ResourceTypeIconBubble
                    resourceType={resourceType}
                    icon={icon}
                    className="h-6 w-6 shrink-0 rounded"
                    iconClassName="size-3.5 text-inherit"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-foreground">
                    {member.title}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    In {location}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onDismissMember(member.type, member.id)}
                  aria-label={`Remove ${member.title} from this suggestion`}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            );
          })}
          {subfolders}
        </div>
      ) : null}
    </section>
  );
};
